import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { fetchComplianceJson } from "./complianceFetch";
import { complianceQueryAtom } from "./complianceLive";

export type ReviewKind = "suggestion" | "finding";
const SummarySchema = Schema.Struct({
  status: Schema.Literals(["missing", "generating", "ready", "failed"]),
  text: Schema.NullOr(Schema.String),
  generatedAt: Schema.NullOr(Schema.String),
  retryAfterMs: Schema.NullOr(Schema.Number),
});
export type ReviewSummarySnapshot = typeof SummarySchema.Type;
const decode = Schema.decodeUnknownEffect(SummarySchema);
const target = (organizationId: string, kind: ReviewKind, entityId: string) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulation/review-summary", {
    organizationId,
    kind,
    entityId,
  });
export const reviewSummaryAtom = Atom.family((key: string) => {
  const [org, kind, id] = JSON.parse(key) as [string, ReviewKind, string];
  return complianceQueryAtom(
    org,
    Effect.gen(function* () {
      if (!org || !id) return null;
      return yield* fetchComplianceJson(target(org, kind, id), decode);
    }),
  ).pipe(Atom.withLabel(`web-amoreg:review-summary:${key}`));
});

// StrictMode, concurrent views and quick navigation share one generation request.
// The database lease provides the same guarantee across browsers and processes.
const pending = new Map<string, Promise<ReviewSummarySnapshot>>();
export function generateReviewSummary(url: string): Promise<ReviewSummarySnapshot> {
  const existing = pending.get(url);
  if (existing) return existing;
  const request = Promise.resolve()
    .then(async () => {
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        signal: AbortSignal.timeout(20_000),
      });
      if (!response.ok) throw new Error("Summary could not be generated.");
      const data = await Effect.runPromise(decode(await response.json()));
      if (data.status === "ready" && !data.text?.trim()) throw new Error("Summary was empty.");
      return data;
    })
    .finally(() => {
      pending.delete(url);
    });
  pending.set(url, request);
  return request;
}

/** Recover an expired server lease, but never leave a view polling indefinitely. */
export function summaryPollDelay(elapsedMs: number, retryAfterMs: number | null): number | null {
  if (elapsedMs >= 45_000) return null;
  return Math.min(45_000 - elapsedMs, 5000, Math.max(elapsedMs < 5000 ? 1000 : 3000, retryAfterMs ?? 1000));
}

export function useReviewSummary(organizationId: string, kind: ReviewKind, entityId: string) {
  const scope = JSON.stringify([organizationId, kind, entityId]);
  const atom = useMemo(() => reviewSummaryAtom(scope), [scope]);
  const result = useAtomValue(atom);
  const value = AsyncResult.value(result);
  const data = value._tag === "Some" ? value.value : null;
  const snapshot = data && !("unavailable" in data) ? data : null;
  const readFailed = !!(data && "unavailable" in data) || result._tag === "Failure";
  const [failure, setFailure] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(false);
  const polling = useRef<{ scope: string; startedAt: number } | null>(null);
  const url = target(organizationId, kind, entityId);
  const refresh = useCallback(() => appAtomRegistry.refresh(atom), [atom]);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (snapshot?.status === "missing" && organizationId && entityId) {
      setFailure(null);
      void generateReviewSummary(url).then(
        () => {
          if (active) refresh();
        },
        () => {
          if (active) setFailure(scope);
        },
      );
    } else if (snapshot?.status === "generating") {
      if (polling.current?.scope !== scope) polling.current = { scope, startedAt: Date.now() };
      const delay = summaryPollDelay(Date.now() - polling.current.startedAt, snapshot.retryAfterMs);
      if (delay === null) setFailure(scope);
      else timer = setTimeout(refresh, delay);
    } else if (snapshot?.status === "ready" || snapshot?.status === "failed") {
      polling.current = null;
    }
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [snapshot, url, scope, organizationId, entityId, refresh]);

  useEffect(() => {
    const delay =
      snapshot?.status === "failed" ? Math.min(30_000, Math.max(0, snapshot.retryAfterMs ?? 0)) : 0;
    setCooldown(delay > 0);
    const timer = setTimeout(() => setCooldown(false), delay);
    return () => clearTimeout(timer);
  }, [scope, snapshot]);

  const retry = useCallback(async () => {
    if (retrying === scope || cooldown) return;
    polling.current = null;
    setFailure(null);
    if (readFailed) {
      refresh();
      return;
    }
    setRetrying(scope);
    try {
      await generateReviewSummary(url);
      refresh();
    } catch {
      setFailure(scope);
    } finally {
      setRetrying((current) => (current === scope ? null : current));
    }
  }, [scope, retrying, cooldown, readFailed, refresh, url]);
  const failed =
    readFailed ||
    (failure === scope && snapshot?.status !== "ready") ||
    snapshot?.status === "failed" ||
    (snapshot?.status === "ready" && !snapshot.text?.trim());
  return {
    text: snapshot?.status === "ready" ? snapshot.text : null,
    loading: retrying === scope || (!failed && snapshot?.status !== "ready"),
    failed,
    retry,
    retryDisabled: cooldown || retrying === scope,
  };
}
